/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

const generateHeaderNavigationLink =
    (isSpecial) =>
    ({size, theme: {textStyles}}) => {
        const color = isSpecial ? 'stokkePink.dark' : 'stokkeCore.black'
        return {
            px: null,
            color,
            minWidth: '1em',
            textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
            paddingBottom: 'calc(var(--chakra-space-linkUnderlineOffset) / 2 + 0.5px)',
            // use different text style based on button size
            ...(size === 'sm' ? textStyles['Regular Small'] : textStyles['Regular Medium']),
            '@media(pointer: fine)': {
                _hover: {
                    textDecoration: 'underline',
                    _disabled: {
                        color: 'stokkeGray.mid',
                        opacity: null
                    }
                },
                _active: {
                    textDecoration: 'underline'
                }
            },
            _disabled: {
                color: 'stokkeGray.mid',
                opacity: null
            }
        }
    }

const solidVariant = ({colorScheme, theme, textStyle = 'Regular Medium'}) => {
    const {colors, textStyles} = theme

    return colorScheme === 'stokkeCore'
        ? {
              bg: `linear-gradient(to bottom, ${colors.stokkeCore.black} 50%, ${colors.stokkeCore.white} 50%)`,
              backgroundSize: '100% 210%',
              backgroundPosition: '0 0%',
              backgroundRepeat: 'no-repeat',
              transition: '.5s',
              border: '2px solid',
              borderRadius: '40px',
              borderColor: 'stokkeCore.black',
              color: 'stokkeCore.white',
              height: '56px',
              padding: '10px',
              ...textStyles[textStyle],
              '@media(pointer: fine)': {
                  _hover: {
                      backgroundPosition: '0 97%',
                      color: 'stokkeCore.black',
                      _disabled: {
                          bg: 'stokkeGray.mid',
                          borderColor: 'stokkeGray.mid',
                          color: 'stokkeCore.white',
                          opacity: null
                      }
                  },
                  _active: {
                      bg: 'stokkeCore.white',
                      color: 'stokkeCore.black',
                      border: '2px solid'
                  }
              },
              _disabled: {
                  bg: 'stokkeGray.mid',
                  borderColor: 'stokkeGray.mid',
                  color: 'stokkeCore.white',
                  opacity: null
              }
          }
        : {
              bg: `linear-gradient(to bottom, ${colors[colorScheme].dark} 50%, ${colors[colorScheme].bright} 50%)`,
              backgroundSize: '100% 210%',
              backgroundPosition: '0 0%',
              backgroundRepeat: 'no-repeat',
              transition: '.5s',
              border: '2px solid',
              borderColor: `${colorScheme}.dark`,
              borderRadius: '40px',
              color: 'stokkeCore.white',
              height: '56px',
              padding: '10px',
              ...textStyles[textStyle],
              '@media(pointer: fine)': {
                  _hover: {
                      backgroundPosition: '0 97%',
                      color: `${colorScheme}.dark`,
                      _disabled: {
                          bg: 'stokkeGray.mid',
                          borderColor: 'stokkeGray.mid',
                          color: 'stokkeCore.white',
                          opacity: null
                      }
                  },
                  _active: {
                      bg: 'stokkeCore.white',
                      color: `${colorScheme}.dark`
                  }
              },
              _disabled: {
                  bg: 'stokkeGray.mid',
                  borderColor: 'stokkeGray.mid',
                  color: 'stokkeCore.white',
                  opacity: null
              }
          }
}

export default {
    baseStyle: {
        maxWidth: {
            base: null,
            md: '25rem'
        },
        borderRadius: 'none',
        // remove underline from buttons as links
        textDecoration: 'none',
        _focusVisible: {
            boxShadow: 'outline'
        },
        _hover: {
            textDecoration: 'none'
        }
    },
    variants: {
        solid: solidVariant,
        solidGreen: ({theme, textStyle = 'Regular Medium'}) => {
            const {colors} = theme

            return {
                ...solidVariant({colorScheme: 'stokkeFunctional', theme, textStyle}),
                bg: `linear-gradient(to bottom, ${colors.stokkeFunctional.alertGreen} 50%, ${colors.stokkeCore.white} 50%)`,
                border: '2px solid',
                borderRadius: '40px',
                borderColor: 'stokkeFunctional.alertGreen',
                '@media(pointer: fine)': {
                    _hover: {
                        backgroundPosition: '0 97%',
                        color: 'stokkeFunctional.alertGreen',
                        // hover over disabled is the same as disabled
                        _disabled: {
                            bg: 'stokkeGray.mid',
                            borderColor: 'stokkeGray.mid',
                            color: 'stokkeCore.white',
                            opacity: null
                        }
                    },
                    _active: {
                        bg: 'stokkeCore.white',
                        color: 'stokkeFunctional.alertGreen'
                    }
                }
            }
        },
        babyList: ({theme, textStyle = 'Regular Medium'}) => {
            const {colors} = theme

            return {
                ...solidVariant({colorScheme: 'stokkeBabylist', theme, textStyle}),
                padding: '10px 16px',
                bg: `linear-gradient(to bottom, ${colors.stokkeBabylist.purple} 50%, ${colors.stokkeCore.white} 50%)`,
                border: '2px solid',
                borderRadius: '40px',
                borderColor: 'stokkeBabylist.purple',
                '@media(pointer: fine)': {
                    _hover: {
                        backgroundPosition: '0 97%',
                        color: 'stokkeBabylist.purple',
                        // hover over disabled is the same as disabled
                        _disabled: {
                            bg: 'stokkeGray.mid',
                            borderColor: 'stokkeGray.mid',
                            color: 'stokkeCore.white',
                            opacity: null
                        }
                    },
                    _active: {
                        bg: 'stokkeCore.white',
                        color: 'stokkeBabylist.purple'
                    }
                }
            }
        },
        placeOrder: ({theme, textStyle = 'Regular Medium'}) => {
            const {colors, textStyles} = theme

            return {
                bg: `linear-gradient(to bottom, ${colors.stokkeFunctional.alertGreen} 50%, ${colors.stokkeCore.white} 50%)`,
                backgroundSize: '100% 210%',
                transition: '.5s',
                border: '2px solid',
                borderColor: 'stokkeFunctional.alertGreen',
                borderRadius: '40px',
                color: 'stokkeCore.white',
                width: 'full',
                maxWidth: {
                    base: null,
                    lg: '343px'
                },
                ...textStyles[textStyle],
                '@media(pointer: fine)': {
                    _hover: {
                        backgroundPosition: '0 97%',
                        color: 'stokkeFunctional.alertGreen',
                        // hover over disabled is the same as disabled
                        _disabled: {
                            bg: 'stokkeGray.mid',
                            borderColor: 'stokkeGray.mid',
                            color: 'stokkeCore.white',
                            opacity: null
                        }
                    },
                    _active: {
                        bg: 'stokkeCore.white',
                        color: 'stokkeFunctional.alertGreen'
                    }
                },
                _disabled: {
                    bg: 'stokkeGray.mid',
                    borderColor: 'stokkeGray.mid',
                    color: 'stokkeCore.white',
                    opacity: null
                }
            }
        },
        outline: ({theme, textStyle = 'Regular Medium'}) => {
            const {colors, textStyles} = theme

            return {
                bg: `linear-gradient(to bottom, ${colors.stokkeCore.white} 50%, ${colors.stokkeCore.black} 50%)`,
                backgroundSize: '100% 210%',
                transition: '.5s',
                color: 'stokkeCore.black',
                borderRadius: '40px',
                border: '2px solid',
                borderColor: 'stokkeCore.black',
                height: '56px',
                padding: '10px',
                // chakra doesn't support referencing text styles from theme,
                // so directly inject the style
                ...textStyles[textStyle],
                '@media(pointer: fine)': {
                    _hover: {
                        backgroundPosition: '0 97%',
                        color: 'stokkeCore.white',
                        _disabled: {
                            bg: 'stokkeCore.white',
                            color: 'stokkeGray.mid',
                            borderColor: 'stokkeGray.mid',
                            opacity: null
                        }
                    },
                    _active: {
                        backgroundColor: 'stokkeCore.black',
                        color: 'stokkeCore.white'
                    }
                },
                _disabled: {
                    bg: 'stokkeCore.white',
                    color: 'stokkeGray.mid',
                    borderColor: 'stokkeGray.mid',
                    opacity: null
                }
            }
        },
        swatch: ({theme: {textStyles}, textStyle = 'Regular Extra Small'}) => ({
            borderRadius: 'full',
            padding: 0,
            cursor: 'pointer',
            color: 'stokkeCore.black',
            border: '1px',
            borderColor: 'stokkeCore.black',
            minW: null,
            minWidth: null,
            width: 6,
            height: 6,
            ...textStyles[textStyle],
            _hover: {
                borderColor: 'stokkeCore.black',
                borderStyle: 'solid'
            },
            _active: {
                background: 'transparent'
            }
        }),
        footer: {
            fontSize: 'sm',
            backgroundColor: 'gray.100',
            borderRadius: '40px',
            color: 'black',
            _hover: {bg: 'gray.200'},
            _active: {bg: 'gray.300'},
            paddingLeft: 3,
            paddingRight: 3
        },
        link: ({
            colorScheme: c,
            size,
            textStyle = size === 'sm' ? 'Regular Small' : 'Regular Medium',
            theme
        }) => {
            const {colors, textStyles} = theme
            const color = colors[c]?.accessibleText || colors[c]?.dark || colors['stokkeCore'].black

            return {
                bg: `linear-gradient(to bottom, transparent 50%, ${color} 50%)`,
                backgroundSize: '100% 210%',
                transition: '.5s',
                color,
                textDecoration: 'underline',
                textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
                paddingBottom: 'calc(var(--chakra-space-linkUnderlineOffset) / 2)',
                minWidth: '1em',
                // use different text style based on button size
                ...textStyles[textStyle],
                '@media(pointer: fine)': {
                    _hover: {
                        backgroundPosition: '0 97%',
                        color:
                            c === 'stokkeMonochrome'
                                ? colors.stokkeMonochrome.mid
                                : 'stokkeCore.white',
                        textDecoration: 'none',
                        textDecorationColor: 'stokkeCore.black',
                        _disabled: {
                            color: 'stokkeGray.mid',
                            opacity: null,
                            textDecoration: 'underline'
                        }
                    },
                    _active: {
                        bg: 'stokkeCore.black',
                        color: 'stokkeCore.white',
                        textDecoration: 'none'
                    }
                },
                _disabled: {
                    color: 'stokkeGray.mid',
                    opacity: null,
                    textDecoration: 'underline'
                }
            }
        },
        headerNavigationLink: generateHeaderNavigationLink(false),
        headerNavigationLinkSpecial: generateHeaderNavigationLink(true),
        navigationLink: {
            maxWidth: null,
            px: 2,
            py: 1,
            height: '3.24rem',
            textDecoration: 'none',
            _hover: {
                textDecoration: 'none'
            },
            _focus: {
                outline: '3px solid var(--chakra-colors-stokkeFunctional-focusBlue)!important',
                outlineOffset: '2px !important'
            }
        },
        navigationFooter: ({theme: {textStyles}}) => ({
            ...textStyles['Regular Small'],
            flexDirection: 'column',
            gap: 2,
            width: 'full',
            paddingY: 2,
            textDecoration: 'underline',
            textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
            height: 'auto'
        }),
        localeSelector: ({
            colorScheme: c,
            theme: {colors, textStyles},
            color = colors[c]?.accessibleText || colors[c]?.dark || colors['stokkeCore'].black
        }) => ({
            // remove base styling
            maxWidth: null,
            // remove size styling
            px: null,
            height: 'auto',
            minWidth: null,
            // custom styling
            ...textStyles['Locale Selector'],
            // link styling
            bg: `linear-gradient(to bottom, transparent 50%, ${color} 50%)`,
            backgroundSize: '100% 210%',
            transition: '.5s',
            color,
            textDecoration: {base: 'underline', lg: 'none'},
            textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
            paddingBottom: 'calc(var(--chakra-space-linkUnderlineOffset) / 2)',
            '@media(pointer: fine)': {
                _hover: {
                    backgroundPosition: {base: '0 97%', lg: '0 0'},
                    color: {base: 'stokkeCore.white', lg: 'stokkeCore.black'},
                    textDecoration: {base: 'none', lg: 'underline'},
                    textDecorationColor: 'stokkeCore.black',
                    _disabled: {
                        color: 'stokkeGray.mid',
                        opacity: null,
                        textDecoration: 'underline'
                    }
                }
            }
        }),
        card: ({colorScheme: c, theme: {textStyles}}) => ({
            backgroundColor: `${c}.mid`,
            color: 'stokkeCore.black',
            textDecoration: 'underline',
            textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
            height: {base: 14, md: 'auto'},
            minWidth: '1em',
            ...textStyles['Regular Medium'],
            _hover: {
                background: `${c}.dark`,
                color: 'stokkeCore.white',
                textDecoration: 'none'
            },
            _active: {
                background: `${c}.dark`,
                color: 'stokkeCore.white',
                textDecoration: 'none'
            }
        }),
        blackCard: ({theme: {textStyles}}) => ({
            backgroundColor: `stokkeCore.black`,
            color: 'stokkeCore.white',
            border: '2px solid',
            borderColor: {base: 'stokkeCore.black', md: 'transparent'},
            textDecoration: 'underline',
            textUnderlineOffset: 'var(--chakra-space-linkUnderlineOffset)',
            height: {base: 14, md: 'auto'},
            minWidth: '1em',
            ...textStyles['Regular Medium'],
            _hover: {
                background: `stokkeCore.white`,
                color: 'stokkeCore.black',
                textDecoration: 'none'
            },
            _active: {
                background: `stokkeCore.white`,
                color: 'stokkeCore.black',
                textDecoration: 'none'
            }
        }),
        helpfulness: ({theme: {textStyles}}) => ({
            // button doesn't support textStyle prop,
            // so directly inject the style
            ...textStyles['Regular Medium'],
            padding: '7px',
            border: '1px solid',
            '+ .chakra-button': {
                marginLeft: '-1px'
            },
            '.chakra-button__icon': {
                paddingTop: '1px',
                paddingX: '1px',
                paddingBottom: '2px'
            },
            _active: {
                padding: '6px',
                border: '2px solid',
                zIndex: 1,
                _disabled: {
                    opacity: null,
                    color: 'stokkeCore.black'
                }
            },
            _disabled: {
                opacity: null,
                color: 'stokkeGray.accessibleText',
                _active: {
                    padding: '6px',
                    border: '2px solid'
                }
            }
        }),
        'menu-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {bg: 'gray.50', textDecoration: 'underline', textDecorationColor: 'gray.900'},
            _activeLink: {
                bg: 'gray.50',
                borderLeft: 'solid',
                borderLeftColor: 'gray.600',
                borderLeftWidth: '4px'
            }
        },
        'menu-link-mobile': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {bg: 'gray.50', textDecoration: 'underline', textDecorationColor: 'gray.900'},
            _activeLink: {
                bg: 'gray.100',
                border: 'solid',
                borderColor: 'gray.600',
                borderWidth: '1px'
            }
        },
        'search-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: {textDecoration: 'none'}
        },
        // used for pdp image gallery overlay image buttons
        pillOnTile: {
            bg: 'stokkeCore.white',
            color: 'stokkeCore.black',
            border: '1px',
            borderColor: `stokkeGray.bright`,
            borderRadius: 'full',
            _hover: {
                bg: 'stokkeGray.bright'
            },
            width: 10,
            height: 10,
            minWidth: 10
        },
        hotspot: {
            bg: 'transparent',
            color: 'stokkeCore.orange',
            border: null,
            borderRadius: 'full',
            _hover: {
                color: 'stokkeCore.black'
            },
            _active: {
                color: 'stokkeCore.black'
            },
            width: 10,
            height: 10,
            minWidth: 10
        },
        newsletterSubscribe: {
            display: 'flex',
            height: 4,
            width: 4,
            minWidth: 'unset',
            marginRight: 2
        },
        tag: ({theme: {textStyles}}) => ({
            borderRadius: '30px',
            height: 8.5,
            paddingX: 4,
            paddingY: 2,
            background: 'stokkeGray.bright',
            ...textStyles['Regular Small'],
            color: 'stokkeCore.black'
        }),
        zoom: {
            width: 10,
            height: 10,
            background: 'stokkeCore.black',
            borderRadius: '100%'
        },
        tile: {
            px: 0,
            minWidth: 0,
            maxWidth: null,
            height: 'auto'
        },
        teaserLink: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 4,
            width: '100%',
            paddingX: 4,
            paddingY: 8,
            height: 'initial',
            maxWidth: null,
            bgColor: 'stokkeCore.white',
            textDecoration: 'none',
            _hover: {
                textDecoration: 'none'
            }
        },
        cartSecondary: {
            backgroundColor: 'stokkeGray.bright',
            width: '2.75rem',
            height: '2.75rem',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '2rem'
        },
        linePagination: {
            bg: 'transparent',
            color: 'inherit',
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none',
            padding: 0,
            minW: 0,
            minWidth: 0,
            width: '28px',
            height: '8px',
            flexShrink: 0,
            textDecoration: 'none',
            _focus: {boxShadow: 'none'},
            _focusVisible: {boxShadow: 'outline'},
            _hover: {bg: 'transparent', textDecoration: 'none'},
            _active: {bg: 'transparent'},
            _disabled: {opacity: 1, cursor: 'not-allowed'},
            cursor: 'pointer',
            spacing: 1,
            zIndex: 1,
            justifyContent: 'flex-end'
        }
    },
    sizes: {
        min: {
            width: 'min-content',
            height: 'min-content'
        },
        sm: {
            height: 5
        },
        md: {
            height: 11,
            minWidth: 11
        },
        lg: {
            height: 14
        }
    },
    defaultProps: {
        colorScheme: 'stokkeCore'
    }
}
